import { Component, computed, inject, input, output, DestroyRef, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AlertComponent,
  ButtonComponent,
  InputComponent,
  ModalComponent,
  TextareaComponent,
} from '@verticalbridge/ui-components';
import { RequestAccessService } from '../../../core/services/request-access.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-request-access-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    ButtonComponent,
    InputComponent,
    ModalComponent,
    TextareaComponent,
  ],
  templateUrl: './request-access-modal.component.html',
  styleUrl: './request-access-modal.component.scss'
})
export class RequestAccessModalComponent {
  private requestAccessService = inject(RequestAccessService);
  private destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  // Signal inputs
  isOpen = input(false);
  appName = input('');
  appType = input('');
  userName = input('');
  userEmail = input('');

  closed = output<void>();
  submitted = output<void>();

  // The one place the appType comparison lives — read by both validator
  // effects, submit(), and three times in the template per change-detection
  // cycle, where a getter would re-run each read.
  readonly isVerticalWorks = computed(() => this.appType() === 'verticalWorks');

  // Describes what was done, not what is now true. The strong version ("Your
  // access is active — open Vertical Works from the dashboard tile") outran
  // the evidence: submit() flips isSubmitSuccessful on the POST's `next` and
  // the roles refresh it triggers has its own error branch, which leaves
  // hasAccess false. Any failed refresh — CSP, missing CORS headers, a
  // timeout — then told the user to open an app from a tile still reading
  // Request Access, which looks like a broken app rather than a failed
  // refresh. Built here rather than inline in the template, which read it
  // once per change-detection cycle.
  readonly successMessage = computed(() =>
    this.isVerticalWorks()
      ? 'Your request has been submitted. Access is granted immediately — reload the dashboard if the tile has not updated. An administrator in Vertical Works approves your company before you can bid on work.'
      : 'You will be notified once your request has been reviewed.'
  );

  form = this.fb.group({
    formUserName: ['', Validators.required],
    reason: ['', Validators.required],
    portfolioName: [''],
    companyName: ['']
  });

  isSubmitting = false;
  isSubmitSuccessful = false;
  errorMessage = '';
  alreadySubmittedMessage = '';

  constructor() {
    // Sync userName input to local form state, falling back to email if no real name
    effect(() => {
      const name = this.userName();
      if (name && name !== 'User') {
        this.form.controls.formUserName.setValue(name);
      } else {
        this.form.controls.formUserName.setValue(this.userEmail() || '');
      }
    });

    // Toggle portfolioName validators based on appType
    effect(() => {
      const portfolioNameControl = this.form.controls.portfolioName;
      if (this.appType() === 'partnerPortal') {
        portfolioNameControl.setValidators(Validators.required);
      } else {
        portfolioNameControl.clearValidators();
      }
      portfolioNameControl.updateValueAndValidity();
    });

    // Vertical Works collects a company name instead of a reason: the name
    // is what its approvals queue shows for the pending vendor firm, and its
    // request endpoint has no field for a reason, so asking for one would
    // collect text nothing ever reads.
    effect(() => {
      const companyNameControl = this.form.controls.companyName;
      const reasonControl = this.form.controls.reason;

      if (this.isVerticalWorks()) {
        companyNameControl.setValidators(Validators.required);
        reasonControl.clearValidators();
      } else {
        companyNameControl.clearValidators();
        reasonControl.setValidators(Validators.required);
      }
      companyNameControl.updateValueAndValidity();
      reasonControl.updateValueAndValidity();
    });
  }

  close(): void {
    this.form.controls.reason.reset('');
    this.form.controls.portfolioName.reset('');
    this.form.controls.companyName.reset('');
    this.isSubmitSuccessful = false;
    this.errorMessage = '';
    this.alreadySubmittedMessage = '';
    this.closed.emit();
  }

  // vb-modal emits visibleChange(false) for any close path (X / backdrop /
  // Escape) — some of which don't fire onCancel. Without resetting the parent's
  // open flag here it stays true, and reopening becomes a no-op true->true.
  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.close();
    }
  }

  submit(): void {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      this.errorMessage = 'Please fill in all required fields.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.form.disable();

    const { formUserName, reason, portfolioName, companyName } = this.form.getRawValue();

    // Call the appropriate service method based on app type
    let request$;
    if (this.isVerticalWorks()) {
      request$ = this.requestAccessService.requestVerticalWorksAccess(
        this.userEmail(),
        formUserName!,
        companyName!
      );
    } else if (this.appType() === 'partnerPortal') {
      request$ = this.requestAccessService.requestPartnerPortalAccess(
        this.userEmail(),
        formUserName!,
        reason!,
        portfolioName!
      );
    } else if (this.appType() === 'verticalIntel') {
      request$ = this.requestAccessService.requestVerticalIntelAccess(
        formUserName!,
        this.userEmail(),
        reason!
      );
    } else {
      // Default branch: Site Locator. Online Colo access is granted by
      // default to new accounts and has no Request Access flow.
      request$ = this.requestAccessService.requestSiteLocatorAccess(
        formUserName!,
        this.userEmail(),
        reason!
      );
    }

    request$.pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.isSubmitSuccessful = true;
        this.submitted.emit();
        // Note: User will get updated roles on next login (Auth0 action runs at login time)
      },
      error: (error) => {
        this.isSubmitting = false;
        if (error.status === 409) {
          const submittedAt = error.error?.existingRequestedAt
            ? new Date(error.error.existingRequestedAt).toLocaleDateString()
            : 'earlier';
          this.isSubmitSuccessful = true;
          this.errorMessage = '';
          this.alreadySubmittedMessage = `You already have a pending request submitted on ${submittedAt}. You'll be notified once it's reviewed.`;
          this.submitted.emit();
        } else {
          this.form.enable();
          this.errorMessage = error.error?.message || 'Failed to submit request. Please try again.';
        }
      }
    });
  }
}
